import React from "react";
import { Logo } from "../components/tabon/Logo";

const VENUE_LOGIN_HREF = "https://dashboard.tabon.app";

function Section({ title, children }) {
  return (
    <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-6 mb-4">
      <h2 className="text-lg font-bold text-white mb-3">{title}</h2>
      <div className="text-[#A0A0A0] text-sm leading-relaxed space-y-3">
        {children}
      </div>
    </div>
  );
}

function Li({ children }) {
  return (
    <li className="flex gap-2">
      <span className="text-[#9D00FF] mt-0.5 flex-shrink-0">·</span>
      <span>{children}</span>
    </li>
  );
}

export default function Privacy() {
  return (
    <div className="min-h-screen tb-hero-gradient relative overflow-hidden">
      <div className="absolute inset-0 tb-grid-bg opacity-30 pointer-events-none" />

      {/* Nav */}
      <header className="relative flex items-center justify-between px-6 md:px-12 py-5">
        <a href="/" aria-label="Back to TabOn home">
          <Logo size="md" />
        </a>
        <a
          href="/"
          className="text-sm text-[#A0A0A0] hover:text-white transition"
        >
          ← Back to TabOn
        </a>
      </header>

      {/* Hero */}
      <section className="relative px-6 md:px-12 pt-12 pb-10 max-w-4xl mx-auto">
        <div className="inline-flex items-center gap-2 bg-[#9D00FF]/10 border border-[#9D00FF]/30 rounded-full px-3 py-1 text-xs text-[#D78CFF] mb-4">
          Legal
        </div>
        <h1 className="text-4xl md:text-5xl font-black tracking-tight text-white">
          Privacy Policy
        </h1>
        <p className="text-[#A0A0A0] mt-3 text-sm">
          Last updated: 14 May 2026 · Effective: 14 May 2026
        </p>
      </section>

      {/* Content */}
      <main className="relative px-6 md:px-12 pb-20 max-w-4xl mx-auto">

        <Section title="1. About TabOn">
          <p>
            TabOn (operated by TabOn Labs, "we", "our", "us") is a loyalty and
            engagement platform for restaurants, cafés, bars, and lounges across
            India. We provide venue partners with tools to manage customer loyalty
            points, bottle lockers, WhatsApp communications, and QR menus.
          </p>
          <p>
            This Privacy Policy explains how we collect, use, store, and protect
            personal data when you interact with TabOn — whether through a venue's
            QR menu, a loyalty registration, or WhatsApp messages sent by a venue
            using TabOn's platform.
          </p>
          <p>
            TabOn Labs operates at{" "}
            <a href="https://tabon.app" className="text-[#9D00FF] hover:underline">
              tabon.app
            </a>{" "}
            and can be reached at{" "}
            <a
              href="mailto:cheers@tabon.app"
              className="text-[#9D00FF] hover:underline"
            >
              cheers@tabon.app
            </a>
            .
          </p>
        </Section>

        <Section title="2. Information We Collect">
          <p>We collect only what is necessary to run your loyalty account:</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm mt-2 border-collapse">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-2 pr-4 text-white font-semibold w-32">Category</th>
                  <th className="text-left py-2 pr-4 text-white font-semibold">Data</th>
                  <th className="text-left py-2 text-white font-semibold">How collected</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {[
                  ["Identity", "Name, phone number, date of birth", "Provided during loyalty registration"],
                  ["Transaction", "Visit dates, bill amounts, points earned and redeemed, order type", "Recorded by venue staff at billing"],
                  ["Loyalty", "Points balance, milestone rewards, stamp card progress", "Calculated from transaction records"],
                  ["Bottle Locker", "Brand, quantity, peg history", "Recorded by staff when a tab is opened"],
                  ["Feedback", "Star rating, optional comment", "Provided voluntarily"],
                  ["Communication", "WhatsApp delivery status", "Logged by Meta's WhatsApp Business API"],
                ].map(([cat, data, how]) => (
                  <tr key={cat}>
                    <td className="py-2 pr-4 text-white font-medium align-top">{cat}</td>
                    <td className="py-2 pr-4 align-top">{data}</td>
                    <td className="py-2 align-top">{how}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="bg-[#9D00FF]/10 border border-[#9D00FF]/20 rounded-lg px-4 py-3 mt-3">
            <p className="text-[#D78CFF] text-xs font-semibold mb-1">We do NOT collect:</p>
            <p className="text-[#A0A0A0] text-xs">
              Payment card details · Government ID numbers · Passwords in plain text · Precise location data
            </p>
          </div>
        </Section>

        <Section title="3. How We Use Your Information">
          <p>We use data collected solely to:</p>
          <ul className="space-y-1.5 mt-2">
            <Li>Manage your loyalty account — points, redemptions, expiry</Li>
            <Li>Send transactional WhatsApp messages with your consent</Li>
            <Li>Provide venue partners with their own customer analytics</Li>
            <Li>Prevent fraud and ensure platform security</Li>
            <Li>Comply with applicable Indian law</Li>
          </ul>
          <p className="mt-3 text-[#D78CFF] font-medium">
            We do not use your data for advertising, third-party marketing, or profiling. Your data is never sold.
          </p>
        </Section>

        <Section title="4. WhatsApp Messaging & Consent">
          <p>
            TabOn uses the Meta WhatsApp Business API to send messages on behalf
            of venue partners.
          </p>
          <p>By registering for a loyalty programme at a TabOn venue, you consent to receive:</p>
          <ul className="space-y-1.5 mt-2">
            <Li>Bill confirmation with points earned</Li>
            <Li>Redemption OTP for using your points</Li>
            <Li>Welcome message on first registration</Li>
            <Li>Milestone reward notifications</Li>
            <Li>Points expiry reminders</Li>
            <Li>Feedback requests after a visit</Li>
            <Li>Re-engagement messages if you haven't visited in over 30 days</Li>
          </ul>
          <p className="mt-3">
            <span className="text-white font-semibold">Withdrawing consent:</span> Reply{" "}
            <span className="font-mono bg-white/5 px-1.5 py-0.5 rounded text-xs">STOP</span> to
            any WhatsApp message or ask venue staff to update your preference. OTP messages
            required to complete a transaction cannot be disabled.
          </p>
          <p>
            Messages are subject to{" "}
            <a
              href="https://www.whatsapp.com/legal/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#9D00FF] hover:underline"
            >
              WhatsApp's Privacy Policy
            </a>
            .
          </p>
        </Section>

        <Section title="5. How We Share Information">
          <p>We do not sell personal data. We share information only in these circumstances:</p>
          <p className="mt-2">
            <span className="text-white font-semibold">Venue partners</span> — Each venue sees
            only their own customers' data.
          </p>
          <p className="mt-2 text-white font-semibold">Infrastructure providers:</p>
          <ul className="space-y-1.5 mt-1">
            <Li>MongoDB Atlas — database storage</Li>
            <Li>Cloudinary — image hosting</Li>
            <Li>DigitalOcean — backend hosting (India)</Li>
            <Li>Vercel — frontend hosting</Li>
            <Li>Meta (WhatsApp) — message delivery</Li>
          </ul>
          <p className="mt-2">
            <span className="text-white font-semibold">Legal requirements</span> — If required
            by Indian law, court order, or government authority.
          </p>
        </Section>

        <Section title="6. Data Retention">
          <ul className="space-y-2">
            <Li>
              <span className="text-white font-medium">Active accounts:</span> Retained while your
              loyalty relationship with a venue is active.
            </Li>
            <Li>
              <span className="text-white font-medium">Transaction records:</span> 7 years for
              financial and GST compliance.
            </Li>
            <Li>
              <span className="text-white font-medium">WhatsApp logs:</span> 90 days.
            </Li>
            <Li>
              <span className="text-white font-medium">Deleted accounts:</span> Personal
              identifiers removed within 30 days of a verified erasure request.
            </Li>
          </ul>
        </Section>

        <Section title="7. Security">
          <p>We implement:</p>
          <ul className="space-y-1.5 mt-2">
            <Li>TLS/HTTPS encryption for all data in transit</Li>
            <Li>Access controls for authorised personnel only</Li>
            <Li>OTP verification for high-value operations</Li>
            <Li>Regular security reviews</Li>
          </ul>
          <p className="mt-3">
            Contact{" "}
            <a href="mailto:cheers@tabon.app" className="text-[#9D00FF] hover:underline">
              cheers@tabon.app
            </a>{" "}
            immediately if you suspect a security breach.
          </p>
        </Section>

        <Section title="8. Your Rights Under DPDP Act 2023">
          <p>Under India's Digital Personal Data Protection Act 2023:</p>
          <div className="space-y-2 mt-2">
            {[
              ["Right to information", "Know what data we hold and how we use it."],
              ["Right to correction", "Request correction of inaccurate data."],
              ["Right to erasure", "Request deletion of your personal data."],
              ["Right to grievance redressal", "Lodge a complaint and receive response within 30 days."],
              ["Right to nominate", "Nominate someone to exercise rights on your behalf."],
              ["Right to withdraw consent", "Opt out of WhatsApp communications at any time."],
            ].map(([right, desc]) => (
              <div key={right} className="flex gap-3">
                <span className="text-[#9D00FF] font-semibold text-xs mt-0.5 flex-shrink-0 w-40">{right}</span>
                <span>{desc}</span>
              </div>
            ))}
          </div>
        </Section>

        <Section title="9. Grievance Redressal">
          <p>
            In accordance with the DPDP Act 2023 and IT Act 2000:
          </p>
          <div className="bg-[#9D00FF]/10 border border-[#9D00FF]/30 rounded-xl p-5 mt-3">
            <p className="text-white font-bold mb-1">Grievance Officer</p>
            <p>TabOn Labs</p>
            <p>
              Email:{" "}
              <a href="mailto:cheers@tabon.app" className="text-[#9D00FF] hover:underline">
                cheers@tabon.app
              </a>
            </p>
            <p>
              Website:{" "}
              <a href="https://tabon.app" className="text-[#9D00FF] hover:underline">
                tabon.app
              </a>
            </p>
            <p className="mt-2 text-xs text-[#D78CFF]">Response time: Within 30 days</p>
          </div>
        </Section>

        <Section title="10. Children's Privacy">
          <p>
            TabOn's services are not directed at individuals under 18. We do not
            knowingly collect data from minors. Contact{" "}
            <a href="mailto:cheers@tabon.app" className="text-[#9D00FF] hover:underline">
              cheers@tabon.app
            </a>{" "}
            if you believe we have.
          </p>
        </Section>

        <Section title="11. Changes to This Policy">
          <p>
            We may update this policy periodically. Material changes will be
            communicated via WhatsApp to registered users where possible. Continued
            use after changes constitutes acceptance.
          </p>
        </Section>

        <Section title="12. Contact">
          <p>
            TabOn Labs ·{" "}
            <a href="mailto:cheers@tabon.app" className="text-[#9D00FF] hover:underline">
              cheers@tabon.app
            </a>{" "}
            ·{" "}
            <a href="https://tabon.app" className="text-[#9D00FF] hover:underline">
              tabon.app
            </a>
          </p>
        </Section>
      </main>

      {/* Footer */}
      <footer className="relative border-t border-white/5">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-10 grid gap-8 md:grid-cols-3 items-start">
          <div>
            <Logo size="md" />
            <p className="text-sm text-[#A0A0A0] mt-3">Turn Every Visit Into The Next One.</p>
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-2 md:justify-center">
            <a href="/" className="text-sm text-[#A0A0A0] hover:text-white transition">Home</a>
            <a
              href={VENUE_LOGIN_HREF}
              className="text-sm text-[#A0A0A0] hover:text-white transition"
            >
              Venue Login
            </a>
            <a href="/privacy" className="text-sm text-[#A0A0A0] hover:text-white transition">
              Privacy Policy
            </a>
          </div>
          <div className="md:text-right">
            <a
              href="mailto:cheers@tabon.app"
              className="text-sm text-white hover:text-[#9D00FF] transition"
            >
              cheers@tabon.app
            </a>
          </div>
        </div>
        <div className="border-t border-white/5 py-4">
          <div className="max-w-6xl mx-auto px-6 md:px-12 text-xs text-[#A0A0A0] text-center">
            <div>
              © 2026 TabOn Labs · UDYAM-RJ-33-0131727 · Udaipur, Rajasthan ·{" "}
              <a href="mailto:cheers@tabon.app" className="hover:text-white transition">
                cheers@tabon.app
              </a>
            </div>
            <div className="text-[11px] text-[#6b6b6b] mt-1">
              TabOn Labs is registered under the Ministry of Micro, Small and Medium Enterprises, Government of India.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
